import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, API } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DatePickerField } from '../components/DatePickerField';
import { ArrowLeft, Download, Lock, Unlock, Plus, Trash2, Edit, Link2, Calculator, FileText, AlertCircle, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';
import { downloadAuthedBlob } from '../lib/download';

const CATEGORIES = [
  'Transport', 'Power', 'Operators', 'Squad', 'Offloading', 'Rental',
  'Packaging Labour', 'Packaging Paper', 'Bailers', 'Commission', 'Miscellaneous', 'Other'
];

const fmt = (n) => new Intl.NumberFormat('en-KE').format(Number(n || 0));
const fmtKES = (n) => new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0 }).format(n || 0);
const fmt2 = (n) => Number(n || 0).toFixed(2);

export default function BatchReport() {
  const { purchaseId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [templates, setTemplates] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, e, t] = await Promise.all([
        api.get(`/batches/${purchaseId}`),
        api.get('/expenses').catch(() => ({ data: [] })),
        api.get('/costing-templates').catch(() => ({ data: [] })),
      ]);
      setReport(r.data);
      setExpenses(e.data || []);
      setTemplates(t.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load batch report');
    } finally {
      setLoading(false);
    }
  }, [purchaseId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]" /></div>;
  if (!report) return <div className="p-8 text-center text-slate-500">Batch not found.</div>;

  const isClosed = report.batch_status === 'Closed';
  const fin = report.financials;
  const agg = report.aggregate_production;
  const an = report.analytics;

  return (
    <div className="space-y-6" data-testid="batch-report-page">
      <Header report={report} onDownloadPdf={() => {
        const safe = (report.purchase.batch_no || 'batch').replace(/[^A-Za-z0-9_\-]/g, '_');
        return downloadAuthedBlob(`/batches/${purchaseId}/pdf`, `Primefield_Batch_Report_${safe}.pdf`);
      }} onClose={load} onReopen={load} />
      <FinancialSummaryCards fin={fin} isProvisional={!report.is_final} />
      <CompletenessCard report={report} onFlagsChanged={load} disabled={isClosed} />
      <PurchaseCard purchase={report.purchase} />
      <ProductionCard productions={report.productions} agg={agg} />
      <ByproductsCard byproducts={report.byproducts} totalRev={fin.byproduct_revenue} />
      <RevenueAllocationCard report={report} purchaseId={purchaseId} onChanged={load} disabled={isClosed} />
      <BatchExpensesCard report={report} purchaseId={purchaseId} expenses={expenses} templates={templates} onChanged={load} disabled={isClosed} />
      <AnalyticsCard an={an} agg={agg} report={report} />
    </div>
  );
}

// ---------------- Header ----------------
function Header({ report, onDownloadPdf, onClose, onReopen }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isClosed = report.batch_status === 'Closed';
  const readyToClose = report.batch_status === 'Ready to Close';
  const p = report.purchase;

  const doClose = async () => {
    try {
      await api.post(`/batches/${p.id}/close`);
      toast.success('Batch closed');
      setConfirmOpen(false);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Close failed'); }
  };
  const doReopen = async () => {
    if (!confirm('Reopen this batch? You will be able to edit management values again.')) return;
    try {
      await api.post(`/batches/${p.id}/reopen`);
      toast.success('Batch reopened');
      onReopen();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reopen failed'); }
  };

  const statusBadge = isClosed
    ? <Badge className="bg-slate-700 text-white">Closed</Badge>
    : readyToClose
      ? <Badge className="bg-amber-500 text-white">Ready to Close</Badge>
      : <Badge className="bg-blue-500 text-white">Open</Badge>;
  const resultBadge = report.financial_result === 'Profit'
    ? <Badge className="bg-emerald-600 text-white">Profit</Badge>
    : report.financial_result === 'Loss'
      ? <Badge className="bg-red-600 text-white">Loss</Badge>
      : <Badge className="bg-slate-500 text-white">Break-even</Badge>;

  return (
    <div className="flex items-start justify-between flex-wrap gap-3">
      <div className="flex items-center gap-3">
        <Link to="/purchases"><Button variant="ghost" size="sm" className="gap-1"><ArrowLeft className="h-4 w-4" /> Back</Button></Link>
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#1B4D3E]" data-testid="batch-title">Batch Report</h1>
          <p className="text-sm text-slate-600 mt-1 flex items-center flex-wrap gap-2">
            <span className="font-medium">{p.batch_no}</span>
            <span>·</span>
            <span>{p.supplier_name || '—'}</span>
            <span>·</span>
            <span>{p.date}</span>
            <span>·</span>
            <span>{p.factory || '—'}</span>
          </p>
          <div className="flex gap-2 mt-2">
            <span className="text-xs text-slate-500">Status:</span>{statusBadge}
            {isClosed && <><span className="text-xs text-slate-500 ml-2">Result:</span>{resultBadge}</>}
            {!report.is_final && <Badge variant="outline" className="text-xs">Provisional</Badge>}
            {report.is_final && <Badge variant="outline" className="text-xs border-emerald-600 text-emerald-700">Final</Badge>}
          </div>
        </div>
      </div>
      <div className="flex gap-2 items-center">
        <Button variant="outline" size="sm" onClick={onDownloadPdf} data-testid="download-batch-pdf" className="gap-1"><Download className="h-4 w-4" /> Download PDF</Button>
        {isClosed ? (
          <Button size="sm" variant="outline" onClick={doReopen} data-testid="reopen-batch-btn" className="gap-1"><Unlock className="h-4 w-4" /> Reopen</Button>
        ) : (
          <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <DialogTrigger asChild>
              <Button size="sm" disabled={!readyToClose} data-testid="close-batch-btn" className="gap-1 bg-[#1B4D3E]"><Lock className="h-4 w-4" /> Close Batch</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Confirm Close Batch</DialogTitle></DialogHeader>
              <div className="text-sm space-y-2">
                <p>Once closed, management P&L will be locked (snapshotted). You can reopen later.</p>
                <div className="grid grid-cols-2 gap-3 mt-3 p-3 bg-slate-50 rounded">
                  <div><div className="text-xs text-slate-500">Total Revenue</div><div className="font-semibold">{fmtKES(report.financials.total_revenue)}</div></div>
                  <div><div className="text-xs text-slate-500">Total Cost</div><div className="font-semibold">{fmtKES(report.financials.total_cost)}</div></div>
                  <div><div className="text-xs text-slate-500">Profit / Loss</div><div className={`font-semibold ${report.financials.profit_loss >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtKES(report.financials.profit_loss)}</div></div>
                  <div><div className="text-xs text-slate-500">Margin %</div><div className="font-semibold">{fmt2(report.financials.profit_margin_pct)}%</div></div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
                <Button onClick={doClose} data-testid="confirm-close-batch" className="bg-[#1B4D3E]">Confirm & Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}

// ---------------- Financial Summary ----------------
function FinancialSummaryCards({ fin, isProvisional }) {
  const pl = fin.profit_loss;
  const plColor = pl > 0 ? 'text-emerald-700' : pl < 0 ? 'text-red-700' : 'text-slate-700';
  const cards = [
    { label: 'Maize Purchase Cost', val: fmtKES(fin.maize_purchase_cost), tone: 'text-slate-800' },
    { label: 'Operating Expenses', val: fmtKES(fin.operating_expenses), tone: 'text-slate-800' },
    { label: 'Total Cost', val: fmtKES(fin.total_cost), tone: 'text-red-700 font-semibold' },
    { label: 'Flour Revenue', val: fmtKES(fin.flour_revenue), tone: 'text-slate-800' },
    { label: 'Byproduct Revenue', val: fmtKES(fin.byproduct_revenue), tone: 'text-slate-800' },
    { label: 'Total Revenue', val: fmtKES(fin.total_revenue), tone: 'text-emerald-700 font-semibold' },
    { label: 'Net Profit / Loss', val: fmtKES(pl), tone: plColor + ' font-bold', testid: 'summary-profit-loss' },
    { label: 'Profit Margin %', val: `${fmt2(fin.profit_margin_pct)}%`, tone: plColor + ' font-semibold' },
  ];
  return (
    <div>
      {isProvisional && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-2 flex items-center gap-2"><AlertCircle className="h-3.5 w-3.5" />Provisional — batch not yet closed. Values are live.</div>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map(c => (
          <Card key={c.label}><CardContent className="p-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">{c.label}</div>
            <div className={`text-lg mt-1 ${c.tone}`} data-testid={c.testid}>{c.val}</div>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}

// ---------------- Completeness ----------------
function CompletenessCard({ report, onFlagsChanged, disabled }) {
  const c = report.completeness;
  const flags = report.review_flags;
  const items = [
    { key: 'purchase', label: 'Purchase' },
    { key: 'production', label: 'Production' },
    { key: 'revenue', label: 'Revenue' },
    { key: 'byproducts', label: 'Byproducts' },
    { key: 'expenses', label: 'Expenses' },
  ];
  const setFlag = async (name, val) => {
    try {
      await api.post(`/batches/${report.purchase_id}/review-flags`, { [name]: val });
      onFlagsChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };
  return (
    <Card><CardHeader className="pb-2"><CardTitle className="text-base">Batch Completeness</CardTitle></CardHeader>
      <CardContent className="pt-0">
        <div className="flex flex-wrap gap-3">
          {items.map(it => (
            <div key={it.key} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border ${c[it.key] ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
              {c[it.key] ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}{it.label}
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-4 items-center">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={flags.byproducts_reviewed} onCheckedChange={(v) => setFlag('byproducts_reviewed', !!v)} disabled={disabled} data-testid="byproducts-reviewed-cb" />
            Byproducts reviewed (allows legitimate zero)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={flags.expenses_reviewed} onCheckedChange={(v) => setFlag('expenses_reviewed', !!v)} disabled={disabled} data-testid="expenses-reviewed-cb" />
            Expenses reviewed (allows legitimate zero)
          </label>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------- Purchase ----------------
function PurchaseCard({ purchase }) {
  return (
    <Card><CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Link2 className="h-4 w-4 text-emerald-700" /> Purchase</CardTitle></CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <Info label="Batch No" value={purchase.batch_no} />
          <Info label="Supplier" value={purchase.supplier_name || '-'} />
          <Info label="Purchase Date" value={purchase.date} />
          <Info label="Factory" value={purchase.factory || '-'} />
          <Info label="Kg Purchased" value={`${fmt(purchase.kg_purchased)} kg`} />
          <Info label="Total Maize Cost" value={fmtKES(purchase.total_cost)} />
          <Info label="Maize Cost / kg" value={fmtKES(purchase.maize_cost_per_kg)} />
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------- Production ----------------
function ProductionCard({ productions, agg }) {
  return (
    <Card><CardHeader className="pb-2"><CardTitle className="text-base">Production ({productions.length} run{productions.length !== 1 ? 's' : ''})</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Info label="Total Bales" value={fmt(agg.total_bales)} />
          <Info label="Total Packets" value={fmt(agg.total_packets)} />
          <Info label="Total Flour Weight" value={`${fmt(agg.total_flour_kg)} kg`} />
          <Info label="Total Byproduct" value={`${fmt(agg.total_byproduct_kg)} kg`} />
          <Info label="Actual Extraction %" value={`${fmt2(agg.actual_extraction_pct)}%`} />
          <Info label="Actual Byproduct %" value={`${fmt2(agg.actual_byproduct_pct)}%`} />
          <Info label="Bale Equivalents" value={fmt(agg.total_bale_equivalents)} />
        </div>
        {productions.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Pack</TableHead><TableHead className="text-right">Bales</TableHead>
                <TableHead className="text-right">Packets</TableHead><TableHead className="text-right">Wt Packed</TableHead>
                <TableHead>Team</TableHead><TableHead className="text-right">Pkg Cost/Bale</TableHead><TableHead>Remarks</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {productions.map(p => (
                  <TableRow key={p.id}>
                    <TableCell><Link to={`/production/${p.id}`} className="text-emerald-700 hover:underline">{p.date}</Link></TableCell>
                    <TableCell>{p.pack_size || '-'}</TableCell>
                    <TableCell className="text-right">{fmt(p.num_bales)}</TableCell>
                    <TableCell className="text-right">{fmt(p.total_packets)}</TableCell>
                    <TableCell className="text-right">{p.total_weight_packed ? `${fmt(p.total_weight_packed)} kg` : '-'}</TableCell>
                    <TableCell className="text-sm">{p.packaging_team_name || '-'}</TableCell>
                    <TableCell className="text-right">{p.packaging_cost_per_bale ? fmtKES(p.packaging_cost_per_bale) : '-'}</TableCell>
                    <TableCell className="text-xs text-slate-600 max-w-[200px] truncate">{p.remarks || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------- Byproducts ----------------
function ByproductsCard({ byproducts, totalRev }) {
  const totalQty = byproducts.reduce((s, b) => s + Number(b.quantity_kg || 0), 0);
  return (
    <Card><CardHeader className="pb-2"><CardTitle className="text-base flex items-center justify-between">
      <span>Byproducts</span>
      <span className="text-xs font-normal text-slate-500">Total: <b>{fmt(totalQty)} kg</b> · Revenue: <b className="text-emerald-700">{fmtKES(totalRev)}</b></span>
    </CardTitle></CardHeader>
      <CardContent>
        {byproducts.length === 0 ? <p className="text-sm text-slate-400 italic">No byproducts recorded.</p> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Type</TableHead><TableHead>Production Date</TableHead>
              <TableHead className="text-right">Qty (kg)</TableHead>
              <TableHead className="text-right">Unit Price / kg</TableHead>
              <TableHead className="text-right">Total Value</TableHead>
              <TableHead>Notes</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {byproducts.map((bp, i) => (
                <TableRow key={i}>
                  <TableCell><Badge variant="outline">{bp.name}</Badge></TableCell>
                  <TableCell>{bp.production_date}</TableCell>
                  <TableCell className="text-right">{fmt(bp.quantity_kg)}</TableCell>
                  <TableCell className="text-right">{bp.unit_price_per_kg ? fmtKES(bp.unit_price_per_kg) : <span className="text-amber-600">missing</span>}</TableCell>
                  <TableCell className="text-right font-medium">{bp.total_value ? fmtKES(bp.total_value) : <span className="text-amber-600">missing</span>}</TableCell>
                  <TableCell className="text-xs text-slate-600">{bp.notes || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------- Revenue Allocation ----------------
function RevenueAllocationCard({ report, purchaseId, onChanged, disabled }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ date: new Date(), pack_size: '', quantity: '', selling_price_per_unit: '', remarks: '' });

  const openNew = () => { setEditing(null); setForm({ date: new Date(), pack_size: '', quantity: '', selling_price_per_unit: '', remarks: '' }); setDialogOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ date: new Date(a.date), pack_size: a.pack_size, quantity: a.quantity, selling_price_per_unit: a.selling_price_per_unit, remarks: a.remarks || '' }); setDialogOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    const body = { date: format(form.date, 'yyyy-MM-dd'), pack_size: form.pack_size, quantity: parseFloat(form.quantity), selling_price_per_unit: parseFloat(form.selling_price_per_unit), remarks: form.remarks || null };
    try {
      if (editing) await api.put(`/batches/${purchaseId}/revenue-allocations/${editing.id}`, body);
      else await api.post(`/batches/${purchaseId}/revenue-allocations`, body);
      setDialogOpen(false); onChanged();
      toast.success('Saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };
  const remove = async (id) => {
    if (!confirm('Delete this allocation?')) return;
    try { await api.delete(`/batches/${purchaseId}/revenue-allocations/${id}`); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const packOptions = Object.keys(report.aggregate_production.produced_by_pack || {});
  const totalFlourRev = report.financials.flour_revenue;

  return (
    <Card><CardHeader className="pb-2"><CardTitle className="text-base flex items-center justify-between">
      <span>Sales / Revenue Allocation</span>
      <span className="text-xs font-normal text-slate-500">Flour Revenue: <b className="text-emerald-700">{fmtKES(totalFlourRev)}</b></span>
    </CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-2 text-xs">
          {packOptions.map(ps => {
            const produced = report.aggregate_production.produced_by_pack[ps] || 0;
            const allocated = report.allocated_by_pack[ps] || 0;
            const remaining = report.remaining_by_pack[ps] || 0;
            const isFull = Math.abs(remaining) < 0.001;
            return (
              <div key={ps} className={`p-2 rounded border ${isFull ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="font-medium text-slate-700">{ps}</div>
                <div className="text-slate-500">Produced: <b>{fmt(produced)}</b> · Allocated: <b>{fmt(allocated)}</b> · Remaining: <b>{fmt(remaining)}</b></div>
              </div>
            );
          })}
          {packOptions.length === 0 && <div className="text-slate-400 italic col-span-3">No production runs yet.</div>}
        </div>
        <div>
          <Button size="sm" onClick={openNew} disabled={disabled || packOptions.length === 0} data-testid="add-revenue-alloc-btn" className="gap-1"><Plus className="h-3.5 w-3.5" /> Add Revenue Allocation</Button>
        </div>
        {report.revenue_allocations.length > 0 && (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Pack</TableHead>
              <TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Price/Unit</TableHead>
              <TableHead className="text-right">Total</TableHead><TableHead>Remarks</TableHead>
              {!disabled && <TableHead className="text-center">Actions</TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {report.revenue_allocations.map(a => (
                <TableRow key={a.id} data-testid={`alloc-row-${a.id}`}>
                  <TableCell>{a.date}</TableCell>
                  <TableCell>{a.pack_size}</TableCell>
                  <TableCell className="text-right">{fmt(a.quantity)}</TableCell>
                  <TableCell className="text-right">{fmtKES(a.selling_price_per_unit)}</TableCell>
                  <TableCell className="text-right font-medium">{fmtKES(a.total_revenue)}</TableCell>
                  <TableCell className="text-xs text-slate-600">{a.remarks || '-'}</TableCell>
                  {!disabled && (
                    <TableCell className="text-center">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(a)} data-testid={`edit-alloc-${a.id}`}><Edit className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(a.id)} data-testid={`delete-alloc-${a.id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Revenue Allocation</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Date</Label><DatePickerField date={form.date} onDateChange={(d) => setForm({ ...form, date: d })} /></div>
              <div className="space-y-1"><Label>Pack Size</Label>
                <Select value={form.pack_size} onValueChange={(v) => setForm({ ...form, pack_size: v })}>
                  <SelectTrigger data-testid="alloc-packsize"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{packOptions.map(ps => <SelectItem key={ps} value={ps}>{ps}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Quantity</Label><Input type="number" step="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} required data-testid="alloc-qty" /></div>
              <div className="space-y-1"><Label>Selling Price / Unit</Label><Input type="number" step="0.01" value={form.selling_price_per_unit} onChange={(e) => setForm({ ...form, selling_price_per_unit: e.target.value })} required data-testid="alloc-price" /></div>
            </div>
            <div className="space-y-1"><Label>Remarks</Label><Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-alloc-btn" className="bg-[#1B4D3E]">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---------------- Batch Expenses ----------------
function BatchExpensesCard({ report, purchaseId, expenses, templates, onChanged, disabled }) {
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [costForm, setCostForm] = useState({ date: new Date(), category: '', description: '', amount: '', remarks: '' });
  const [selectedExpense, setSelectedExpense] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [replaceImport, setReplaceImport] = useState(false);

  const linkedExpenseIds = useMemo(() => new Set(report.linked_expenses.map(e => e.expense_id)), [report.linked_expenses]);
  const availableExpenses = expenses.filter(e => !linkedExpenseIds.has(e.id));

  const openNewCost = () => { setEditingCost(null); setCostForm({ date: new Date(), category: '', description: '', amount: '', remarks: '' }); setCostDialogOpen(true); };
  const openEditCost = (c) => { setEditingCost(c); setCostForm({ date: new Date(c.date), category: c.category, description: c.description || '', amount: c.amount, remarks: c.remarks || '' }); setCostDialogOpen(true); };

  const saveCost = async (e) => {
    e.preventDefault();
    const body = { date: format(costForm.date, 'yyyy-MM-dd'), category: costForm.category, description: costForm.description, amount: parseFloat(costForm.amount), remarks: costForm.remarks || null };
    try {
      if (editingCost) await api.put(`/batches/${purchaseId}/costs/${editingCost.id}`, body);
      else await api.post(`/batches/${purchaseId}/costs`, body);
      setCostDialogOpen(false); onChanged();
      toast.success('Saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };
  const removeCost = async (id) => {
    if (!confirm('Delete this batch cost?')) return;
    try { await api.delete(`/batches/${purchaseId}/costs/${id}`); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };
  const linkExpense = async () => {
    if (!selectedExpense) return;
    try { await api.post(`/batches/${purchaseId}/expense-links`, { expense_id: selectedExpense }); setLinkDialogOpen(false); setSelectedExpense(''); onChanged(); toast.success('Linked'); } catch (e) { toast.error(e.response?.data?.detail || 'Link failed'); }
  };
  const unlinkExpense = async (linkId) => {
    if (!confirm('Remove this expense link (does NOT delete the underlying expense)?')) return;
    try { await api.delete(`/batches/${purchaseId}/expense-links/${linkId}`); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Unlink failed'); }
  };
  const doImport = async () => {
    if (!selectedTemplate) return;
    try {
      const res = await api.post(`/batches/${purchaseId}/import-costing/${selectedTemplate}?replace=${replaceImport}`);
      toast.success(`Imported ${res.data.imported} cost lines`);
      setImportDialogOpen(false); setSelectedTemplate(''); setReplaceImport(false);
      onChanged();
    } catch (e) {
      if (e.response?.status === 409) {
        if (confirm('Estimates from this template are already imported. Replace them?')) {
          try {
            const res = await api.post(`/batches/${purchaseId}/import-costing/${selectedTemplate}?replace=true`);
            toast.success(`Replaced with ${res.data.imported} cost lines`);
            setImportDialogOpen(false); setSelectedTemplate(''); onChanged();
          } catch (err2) { toast.error(err2.response?.data?.detail || 'Import failed'); }
        }
      } else toast.error(e.response?.data?.detail || 'Import failed');
    }
  };

  return (
    <Card><CardHeader className="pb-2"><CardTitle className="text-base flex items-center justify-between">
      <span>Batch Expenses</span>
      <span className="text-xs font-normal text-slate-500">Total: <b className="text-red-700">{fmtKES(report.financials.operating_expenses)}</b></span>
    </CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {!disabled && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={openNewCost} data-testid="add-batch-cost-btn" className="gap-1"><Plus className="h-3.5 w-3.5" /> Add Batch Cost</Button>
            <Button size="sm" variant="outline" onClick={() => setLinkDialogOpen(true)} data-testid="link-expense-btn" className="gap-1"><Link2 className="h-3.5 w-3.5" /> Link Existing Expense</Button>
            <Button size="sm" variant="outline" onClick={() => setImportDialogOpen(true)} data-testid="import-costing-btn" className="gap-1"><Calculator className="h-3.5 w-3.5" /> Import Estimates from Costing Template</Button>
          </div>
        )}

        {(report.batch_costs.length > 0 || report.linked_expenses.length > 0) ? (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Description</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead>Source</TableHead>
              {!disabled && <TableHead className="text-center">Actions</TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {report.batch_costs.map(c => (
                <TableRow key={c.id} data-testid={`batch-cost-row-${c.id}`}>
                  <TableCell>{c.date}</TableCell>
                  <TableCell><Badge variant="outline">{c.category}</Badge></TableCell>
                  <TableCell className="text-xs text-slate-600">{c.description || '-'}</TableCell>
                  <TableCell className="text-right font-medium">{fmtKES(c.amount)}</TableCell>
                  <TableCell><Badge variant="secondary" className={c.source === 'imported' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100'}>{c.source === 'imported' ? 'Imported' : 'Batch'}</Badge></TableCell>
                  {!disabled && (
                    <TableCell className="text-center">
                      <Button variant="ghost" size="sm" onClick={() => openEditCost(c)}><Edit className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => removeCost(c.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {report.linked_expenses.map(l => (
                <TableRow key={l.link_id} data-testid={`linked-exp-row-${l.link_id}`}>
                  <TableCell>{l.date}</TableCell>
                  <TableCell><Badge variant="outline">{l.category}</Badge></TableCell>
                  <TableCell className="text-xs text-slate-600">{l.description || '-'}</TableCell>
                  <TableCell className="text-right font-medium">{fmtKES(l.amount)}</TableCell>
                  <TableCell><Badge className="bg-emerald-100 text-emerald-700">Linked</Badge></TableCell>
                  {!disabled && (
                    <TableCell className="text-center">
                      <Button variant="ghost" size="sm" onClick={() => unlinkExpense(l.link_id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-sm text-slate-400 italic">No batch expenses recorded.</p>
        )}
      </CardContent>

      {/* Add/Edit Batch Cost Dialog */}
      <Dialog open={costDialogOpen} onOpenChange={setCostDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingCost ? 'Edit' : 'Add'} Batch Cost</DialogTitle></DialogHeader>
          <form onSubmit={saveCost} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Date</Label><DatePickerField date={costForm.date} onDateChange={(d) => setCostForm({ ...costForm, date: d })} /></div>
              <div className="space-y-1"><Label>Category</Label>
                <Select value={costForm.category} onValueChange={(v) => setCostForm({ ...costForm, category: v })}>
                  <SelectTrigger data-testid="cost-category"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-1"><Label>Description</Label><Input value={costForm.description} onChange={(e) => setCostForm({ ...costForm, description: e.target.value })} /></div>
              <div className="space-y-1"><Label>Amount</Label><Input type="number" step="0.01" value={costForm.amount} onChange={(e) => setCostForm({ ...costForm, amount: e.target.value })} required data-testid="cost-amount" /></div>
              <div className="space-y-1"><Label>Remarks</Label><Input value={costForm.remarks} onChange={(e) => setCostForm({ ...costForm, remarks: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCostDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-cost-btn" className="bg-[#1B4D3E]">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Link Existing Expense */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Link Existing Expense</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Select an existing Expense record</Label>
            <Select value={selectedExpense} onValueChange={setSelectedExpense}>
              <SelectTrigger data-testid="link-expense-select"><SelectValue placeholder="Choose an expense" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {availableExpenses.length === 0 && <div className="p-2 text-sm text-slate-500">All expenses already linked or none available</div>}
                {availableExpenses.map(e => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.date} · {e.category} · {fmtKES(e.amount)} {e.remarks ? `— ${e.remarks.slice(0, 30)}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">Linking references the existing Expense record. It will NOT be duplicated or modified.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
            <Button onClick={linkExpense} disabled={!selectedExpense} data-testid="save-link-expense-btn" className="bg-[#1B4D3E]">Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Costing */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Import Estimates from Costing Template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Choose a Costing Template</Label>
            <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
              <SelectTrigger data-testid="import-template-select"><SelectValue placeholder="Select a template" /></SelectTrigger>
              <SelectContent>
                {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name} · {t.factory} · {t.pack_size}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">Imports become editable Batch Cost lines. Per-bale/packet lines use actual batch production quantities. This does <b>NOT</b> create Expenses accounting records or modify the source template.</p>
            <label className="flex items-center gap-2 text-xs text-slate-600"><Checkbox checked={replaceImport} onCheckedChange={(v) => setReplaceImport(!!v)} data-testid="replace-import-cb" />Replace previously imported estimates from this template</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>Cancel</Button>
            <Button onClick={doImport} disabled={!selectedTemplate} data-testid="confirm-import-btn" className="bg-[#1B4D3E]">Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---------------- Analytics ----------------
function AnalyticsCard({ an, agg, report }) {
  const groups = [
    { title: 'Financial', items: [
      ['Profit / Bale', fmtKES(an.profit_per_bale)],
      ['Profit / Bale Equivalent', fmtKES(an.profit_per_bale_equivalent)],
      ['Profit / Kg Maize', fmtKES(an.profit_per_kg_maize)],
      ['Revenue / Kg Maize', fmtKES(an.revenue_per_kg_maize)],
      ['Maize Cost / kg', fmtKES(an.maize_cost_per_kg)],
      ['Production Cost / Bale', fmtKES(an.production_cost_per_bale)],
      ['Avg Selling Price / Bale Eq', fmtKES(an.avg_selling_price_per_bale_equivalent)],
      ['Byproduct Revenue % of Total', `${fmt2(an.byproduct_revenue_pct_of_total)}%`],
      ['Operating Expenses % of Revenue', `${fmt2(an.opex_pct_of_revenue)}%`],
      ['Maize Cost % of Total Cost', `${fmt2(an.maize_cost_pct_of_total_cost)}%`],
    ]},
    { title: 'Production', items: [
      ['Kg Maize Purchased', `${fmt(report.purchase.kg_purchased)} kg`],
      ['Flour Kg Produced', `${fmt(agg.total_flour_kg)} kg`],
      ['Byproduct Kg', `${fmt(agg.total_byproduct_kg)} kg`],
      ['Actual Extraction %', `${fmt2(agg.actual_extraction_pct)}%`],
      ['Actual Byproduct %', `${fmt2(agg.actual_byproduct_pct)}%`],
      ['Total Bales', fmt(agg.total_bales)],
      ['Total Packets', fmt(agg.total_packets)],
      ['Bale Equivalents', fmt(agg.total_bale_equivalents)],
    ]},
    { title: 'Commercial', items: [
      ...Object.entries(report.aggregate_production.produced_by_pack || {}).map(([ps, q]) => [`Produced ${ps}`, fmt(q)]),
      ...Object.entries(report.allocated_by_pack || {}).map(([ps, q]) => [`Allocated ${ps}`, fmt(q)]),
      ...Object.entries(report.remaining_by_pack || {}).map(([ps, q]) => [`Remaining ${ps}`, fmt(q)]),
      ['% Revenue-Allocated', `${fmt2(an.pct_revenue_allocated)}%`],
      ['Highest Selling Price', fmtKES(an.highest_selling_price)],
      ['Lowest Selling Price', fmtKES(an.lowest_selling_price)],
    ]},
  ];

  return (
    <Card><CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Detailed Analytics</CardTitle></CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
          {groups.map(g => (
            <div key={g.title}>
              <h4 className="font-heading font-semibold text-[#1B4D3E] mb-2">{g.title}</h4>
              <dl className="space-y-1.5">
                {g.items.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3 border-b border-slate-100 pb-1">
                    <dt className="text-slate-500 text-xs">{k}</dt>
                    <dd className="font-medium text-slate-800 text-xs text-right">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function Info({ label, value }) {
  return <div><p className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</p><p className="text-sm font-medium text-slate-800">{value ?? '-'}</p></div>;
}
